import { useEffect } from 'react';

const OverflowFix: React.FC = () => {
  useEffect(() => {
    // Prevent horizontal scroll - optimized to minimize forced reflows
    // Use CSS-based solution when possible, only check when necessary
    let rafId: number | null = null;
    let lastCheck = 0;
    const CHECK_INTERVAL = 500; // Only check every 500ms max
    
    const preventHorizontalScroll = () => {
      const now = Date.now();
      // Throttle checks to avoid excessive reflows
      if (now - lastCheck < CHECK_INTERVAL) {
        return;
      }
      lastCheck = now;
      
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Use double RAF to ensure layout is stable before reading
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          // Batch all reads first - only read if we haven't set overflow yet
          const bodyOverflow = document.body.style.overflowX;
          const htmlOverflow = document.documentElement.style.overflowX;
          
          // Only check dimensions if overflow is not already set
          if (!bodyOverflow && !htmlOverflow) {
            const scrollWidth = document.documentElement.scrollWidth;
            const clientWidth = document.documentElement.clientWidth;
            
            // Only write if there's actually overflow
            if (scrollWidth > clientWidth) {
              document.body.style.overflowX = 'hidden';
              document.documentElement.style.overflowX = 'hidden';
            }
          }
          
          rafId = null;
        });
      });
    };

    // Use CSS-based solution first (already in index.css)
    // Only use JS check as fallback after content loads
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(preventHorizontalScroll);
      });
    }, 500); // Wait longer for content to stabilize
    
    // Throttle resize events aggressively
    let resizeTimer: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (resizeTimer !== null) {
        clearTimeout(resizeTimer);
      }
      // Debounce resize checks
      resizeTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(preventHorizontalScroll);
        });
        resizeTimer = null;
      }, 300);
    };

    const handleOrientationChange = () => {
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(preventHorizontalScroll);
        });
      }, 300);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      clearTimeout(timer);
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return null;
};

export default OverflowFix; 